import React from "react"
import useAxiosPrivate from "../../api/hooks/useAxiosPrivate"
import { useLocation } from "react-router-dom"

// components
import { PrimeButton } from "components-design-system"
import { Tooltip } from "antd"
import PRSUpload from "components/PRSUpload/PRSUpload"
import PRSUploadErrorMessages from "components/PRSUploadErrorMessages/PRSUploadErrorMessages"
import PRSUploadProgress from "components/PRSUploadProgress/PRSUploadProgress"
import CancelPRSUpload from "components/CancelPRSUpload/CancelPRSUpload"
import Modal from "components/Modal/Modal"

// utils
import getUrl from "api/apiMap"
import apiEndpoints from "api/utils/apiEndpoints"
import { useQueryClient } from "@tanstack/react-query"
import queryKeys from "api/utils/queryKeys"
import { PRS_UPLOAD_TASKS } from "../../constants/tasks"
import useErrors from "api/utils/useErrors"

// context
import useAppState from "context/hooks/useAppState"

import "./usePrsUpload.scss"

const usePrsUpload = ({
  useOnGetPrsUpload,
  useOnPostPrsUpload,
  isUploadDisabled,
  disabledTooltip,
  loadingTooltip,
  prsUploadStudyList,
  gridRef,
  uploadToPrsMsg = "Send all site updates to PRS. If you would like to release data directly to CTG check the checkbox below.",
  callbackAfterUpload = null,
  selectColumnKey = "study_db_id",
  type,
  setSelectedRows,
}) => {
  const { errorHandler } = useErrors()
  const axios = useAxiosPrivate()
  const { pathname } = useLocation()
  const { openToastNotification } = useAppState()

  const [queueId, setQueueId] = React.useState(null)
  const [sitesOnly, setSitesOnly] = React.useState()
  const onPostPrsUpload = useOnPostPrsUpload()
  const queryClient = useQueryClient()
  const { data: pollingData } = useOnGetPrsUpload(
    { queueId, sitesOnly },
    {
      refetchInterval: queueId ? 1000 : null,
      enabled: queueId ? true : false,
    }
  )

  const [isAutoRelease, setIsAutoRelease] = React.useState(false)

  // modals
  const [openModal, setOpenModal] = React.useState(false)
  const [openProgressModal, setProgressModal] = React.useState(false)
  const [modalConfig, setModalConfig] = React.useState({})
  const [uploadComplete, setUploadComplete] = React.useState(false)
  const [isUploading, setIsUploading] = React.useState(false)
  const [isCancelling, setIsCancelling] = React.useState(false)
  const [uploadRecords, setUploadRecords] = React.useState(null)

  const queueIdRef = React.useRef()

  const onOpenModal = (variant, data) => {
    switch (variant) {
      case "prs-upload":
        setModalConfig({
          header: "Submit to PRS",
          subheader: uploadToPrsMsg,
          width: 400,
          children: (
            <PRSUpload
              setOpenModal={setOpenModal}
              onOpenModal={onOpenModal}
              onUploadToPRS={onUploadToPRS}
              type={type}
              gridRef={gridRef}
            />
          ),
        })
        setOpenModal(true)
        break
      case "prs-upload-progress":
        setModalConfig({
          header: "Upload in Progress",
          subheader: "See progress of individual study upload below",
          children: (
            <PRSUploadProgress
              data={uploadRecords}
              setOpenModal={setOpenModal}
            />
          ),
        })
        setOpenModal(true)
        break
      // TODO: delete case "prs-upload-error-messages" and remove PRSUploadErrorMessages if not used
      case "prs-upload-error-messages":
        setModalConfig({
          header: "CTG Upload Error",
          subheader: "There are are [4] values that must be updated in order to upload to CTG.",
          children: (
            <PRSUploadErrorMessages
              data={data}
              setOpenModal={setOpenModal}
            />
          ),
        })
        setOpenModal(true)
        break
      case "cancel-upload-confirmation":
        setModalConfig({
          header: "Cancel Upload?",
          subheader:
            "Are you sure you would like to cancel this upload? Some studies may have already uploaded, any remaining studies will not be uploaded to PRS or publicly released.",
          width: 400,
          children: (
            <CancelPRSUpload
              onCancelUpload={() => onCancelUpload(queueId)}
              setOpenModal={setOpenModal}
            />
          ),
        })
        setOpenModal(true)
        break
      default:
        return
    }
  }

  React.useEffect(() => {
    if (
      (pollingData?.in_progress === false ||
        pollingData?.in_progress === "False" ||
        pollingData?.is_cancelled === true) &&
      queueId
    ) {
      onFormatPollingData(pollingData)
    }
  }, [pollingData, callbackAfterUpload, queueId])

  const onFormatPollingData = (pollingData) => {
    if (callbackAfterUpload) callbackAfterUpload(pollingData)
    onStopPolling(pollingData)
  }

  const warningMsg = () => {
    let showWarning = false
    const selectedNodes = gridRef?.current?.api?.getSelectedNodes()
    for (let i = 0; i < selectedNodes?.length; i++) {
      if (selectedNodes[i].rowIndex % 2 === 0) {
        showWarning = true
        break
      }
    }

    return (
      showWarning && (
        <div className="prs-upload-warning">
          Data changes for this study require approval based on configured rules. Navigate to Activities to create a
          maintenance update activity.
        </div>
      )
    )
  }

  const onUploadToPRS = (values) => {
    setUploadComplete(false)
    setIsUploading(true)
    setOpenModal(false)
    setIsCancelling(false)

    let studyList = prsUploadStudyList || []
    let selectedNodes = gridRef?.current?.api?.getSelectedNodes()

    if (selectedNodes?.length > 0) {
      studyList = selectedNodes.map((rowNode) => rowNode.data[selectColumnKey])
    }

    setIsAutoRelease(values.autorelease)

    let req = {
      stu_id_list: studyList,
      autorelease: values.autorelease,
      update_ver_date: values.update_ver_date,
    }

    if (typeof values?.include_sites === "boolean") {
      req.include_sites = values.include_sites
    }

    let sitesOnly = false

    if (req?.include_sites === true) {
      req.include_protocol = false
      sitesOnly = true
    } else {
      req.include_protocol = true
      req.include_sites = true
    }

    // include sites in study registries tab will always be false
    if (type === PRS_UPLOAD_TASKS.studyRegistryPrsProtocolUpload) {
      req.include_sites = false
      sitesOnly = false
    }

    if (values.isPrsCredentialChecked) {
      req.prs_creds = {
        prs_username: values.username,
        prs_password: values.password,
        prs_organization: values.organization,
      }
    }

    setSitesOnly(sitesOnly)

    onPostPrsUpload(
      {
        body: req,
      },
      {
        onSuccess: (resp) => {
          let { queue_id } = resp.data
          setQueueId(queue_id)
          queueIdRef.current = queue_id
        },
        onError: (resp) => {
          setIsUploading(false)
        },
        onSettled: () => {
          gridRef?.current?.api?.deselectAll()
        },
      }
    )
  }

  const hasErrors = React.useMemo(() => {
    if (uploadRecords && uploadRecords.upload_records) {
      for (let i = 0; i < uploadRecords.upload_records.length; i++) {
        const record = uploadRecords.upload_records[i]
        if (record?.failureMessage || record?.upload_failed) {
          return true
        }
        let validations = record?.upload_report?.validationMessages?.validationMessage
        for (let j = 0; j < validations?.length; j++) {
          let msg = validations[i]
          if (msg?.severity === "ERROR") return true
        }
      }
    } else if (uploadRecords?.task_status?.state === "FAILURE") {
      return true
    }
    return false
  }, [uploadRecords])

  const onCloseSummaryModal = () => {
    setProgressModal(false)
    if (queueIdRef.current) {
      queryClient.removeQueries({ queryKey: queryKeys.siteManagement.prsUpload(queueIdRef.current) })
      queueIdRef.current = null
    }
  }

  const UploadModals = (
    <div className="prs-upload-hook-container">
      {openModal && (
        <Modal
          title={modalConfig.header}
          message={modalConfig.subheader}
          onCancel={() => setOpenModal(false)}
          width={modalConfig.width}
          hideImage={true}
          open={openModal}
          footer={[]}
        >
          {modalConfig.children}
        </Modal>
      )}
      <Modal
        title={uploadComplete ? "Upload Summary" : "Upload in Progress"}
        open={openProgressModal}
        hideImage={true}
        onCancel={onCloseSummaryModal}
        footer={[]}
        width={hasErrors || uploadRecords?.task_status?.state === "FAILURE" ? 900 : 600}
      >
        <div className="subheading">
          {uploadComplete
            ? "The upload is complete. Please see status of uploads below."
            : "See progress of individual study upload below"}
        </div>
        <PRSUploadProgress
          isAutoRelease={isAutoRelease}
          uploadComplete={uploadComplete}
          hasErrors={hasErrors || uploadRecords?.task_status?.state === "FAILURE"}
          data={uploadRecords}
          setOpenModal={setProgressModal}
        />
        <div style={{ marginTop: 16 }}>
          {isUploading && (
            <PrimeButton
              type="link"
              danger
              onClick={() => onOpenModal("cancel-upload-confirmation")}
            >
              Cancel Upload
            </PrimeButton>
          )}
        </div>
      </Modal>
    </div>
  )

  const onCancelUpload = () => {
    setIsCancelling(true)

    let url =
      pathname.includes("/locations/") || pathname.includes("/site-management")
        ? `${apiEndpoints.cancelSitesUpload}?queue-id=${queueIdRef.current}`
        : `${apiEndpoints.cancelProtocolUpload}/?queue-id=${queueIdRef.current}`

    return axios
      .get(getUrl(url))
      .then((resp) => {
        onStopPolling(resp.data)
        queryClient.removeQueries({ queryKey: queryKeys.siteManagement.prsUpload(queueIdRef.current) })
        queueIdRef.current = null
        openToastNotification("info", "NOTE", "Upload cancelled")
      })
      .catch((error) => errorHandler({ error }))
  }

  const onStopPolling = (pollingData) => {
    queueIdRef.current = queueId
    setUploadRecords(pollingData)
    setUploadComplete(true)
    setIsUploading(false)
    setSelectedRows && setSelectedRows([])
    setOpenModal(false)
    setQueueId(null)
    setProgressModal(true)
  }

  const getUploadBtnTooltip = () => {
    if (isUploading && loadingTooltip) {
      return loadingTooltip
    } else if (isUploadDisabled && disabledTooltip) {
      return disabledTooltip
    }
    return
  }

  const UploadButton = (
    <Tooltip title={getUploadBtnTooltip()}>
      <PrimeButton
        icon={<i className="fal fa-cloud-upload icons" />}
        type="primary"
        disabled={isUploadDisabled}
        onClick={() => (isUploading ? setProgressModal(true) : onOpenModal("prs-upload"))}
        loading={isUploading}
        style={{ display: "flex", alignItems: "center" }}
      >
        {isUploading ? "Upload in Progress" : "Submit to PRS"}
      </PrimeButton>
    </Tooltip>
  )

  const CancelButton = (
    <div
      className={`cancel-prs-upload-btn ${isCancelling ? "is-cancelling-prs-upload" : ""}`}
      onClick={() => onOpenModal("cancel-upload-confirmation")}
    >
      Cancel Upload
    </div>
  )

  return {
    isUploading,
    warningMsg,
    UploadModals,
    UploadButton,
    CancelButton,
  }
}

export default usePrsUpload
